import React, {useContext, useEffect, useState} from 'react'
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {NavLink, useHistory} from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import {Delete, Edit, Info, KeyboardArrowDown, KeyboardArrowUp, VideoLibrary} from "@mui/icons-material";
import {Box, CircularProgress, Collapse, IconButton, TablePagination} from "@mui/material";
import useListVideos from "./useListVideos";
import {useStyles} from "./VideoSection.css";
import {StyledTableCell, StyledTableRow} from "../../../../commons/utils/table/StyledTablePrimary.css";
import {AppbarContentContext} from "../../../../commons/routing/appbar/AppbarContentProvider";
import AppbarActionButton from "../../../../commons/elements/buttons/AppbarActionButton";
import ScoredScenesSection from "../../videosScored/overview/ScoredScenesSection";
import AppButton from "../../../../commons/elements/buttons/AppButton";
import usePagination from "../../../../commons/utils/usePagination";
import LoadingProgressIndication from "../../../../commons/elements/progress/loading/LoadingProgressIndication";
import VideoCard from "../card/VideoCard";
import {useIntl} from "react-intl";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import GenericFilter from "../../../../commons/elements/search/genericSearch/GenericFilter";

const VideosSection = () => {
    const classes = useStyles();
    const {setCurrentAppbarContent} = useContext(AppbarContentContext)

    // Pagination for the table handling
    const {page, rowsPerPage, handleChangePage, handleChangeRowsPerPage} = usePagination()

    // Hook providing the list user functionality
    const {
        videos,
        loading,
        handleDeleteVideo,
        deletionInProgress,
        scoredScenesNumbers,
        updateScoredScenesNumberOfVideos,
        deletionAttemptedVideo
    } = useListVideos()

    // Collapsible table state
    const [openIndex, setOpenIndex] = React.useState();

    const history = useHistory();

    // Internationalization for simple strings
    const intl = useIntl();

    // Checking if the edit/saving operations are allowed
    const {isOperationOnObjectAllowed} = useContext(FirebaseAuthContext);

    // State which holds the videos according to the current filter
    const [filteredVideos, setFilteredVideos] = useState([])


    // Setting the appbar content
    useEffect(() => {
        setFilteredVideos(videos)

        setCurrentAppbarContent({
            header: intl.formatMessage({id: 'app.videos.overview.appbar.header', defaultMessage: 'Videos & Scores'}),
            description: intl.formatMessage({
                id: 'app.videos.overview.appbar.description',
                defaultMessage: 'Manage videos and annotate them with scores...'
            }),
            actions: <>
                <AppbarActionButton
                    label={intl.formatMessage({
                        id: 'app.videos.overview.appbar.actions.new',
                        defaultMessage: 'New Video'
                    })}
                    component={NavLink}
                    to='/admin/videos/new'
                    icon={<AddIcon/>}/>
            </>,
            searchContainer: <GenericFilter objects={videos}
                                            onResultsChanged={(results) => setFilteredVideos(results)}
                                            filterDefinitions={[
                                                {
                                                    type: 'select',
                                                    id: 'app.videos.filter.association',
                                                    options: [...new Set(videos.map((v) => v.basics.association))],
                                                    getOptionLabel: (association) =>
                                                        intl.formatMessage({
                                                            id: `app.ids.association.${association}`,
                                                            defaultMessage: association
                                                        }),
                                                    condition: (object, filter) => filter?.includes(object.basics.association) || filter?.length === 0
                                                },
                                                {
                                                    type: 'select',
                                                    id: 'app.videos.filter.discipline',
                                                    options: [...new Set(videos.map((v) => v.basics.discipline))],
                                                    getOptionLabel: (discipline) =>
                                                        intl.formatMessage({
                                                            id: `app.ids.discipline.${discipline}`,
                                                            defaultMessage: discipline
                                                        }),
                                                    condition: (object, filter) => filter?.includes(object.basics.discipline) || filter?.length === 0
                                                },
                                                {
                                                    type: 'select',
                                                    id: 'app.videos.filter.tournament',
                                                    options: [...new Set(videos.map((v) => v.basics.tournament))],
                                                    getOptionLabel: (tournament) => tournament,
                                                    condition: (object, filter) => filter?.includes(object.basics.tournament) || filter?.length === 0
                                                },
                                                {
                                                    type: 'select',
                                                    id: 'app.videos.filter.ageGroup',
                                                    options: [...new Set(videos.map((v) => v.basics.ageGroup))],
                                                    getOptionLabel: (ageGroup) =>
                                                        intl.formatMessage({
                                                            id: `app.ids.ageGroup.${ageGroup}`,
                                                            defaultMessage: ageGroup
                                                        }),
                                                    condition: (object, filter) => filter?.includes(object.basics.ageGroup) || filter?.length === 0
                                                },
                                                {
                                                    type: 'select',
                                                    id: 'app.videos.filter.gender',
                                                    options: [...new Set(videos.map((v) => v.basics.gender))],
                                                    getOptionLabel: (gender) =>
                                                        intl.formatMessage({
                                                            id: `app.ids.gender.${gender}`,
                                                            defaultMessage: gender
                                                        }),
                                                    condition: (object, filter) => filter?.includes(object.basics.gender) || filter?.length === 0
                                                },
                                                {
                                                    type: 'select',
                                                    id: 'app.videos.filter.athlete',
                                                    options: [...new Set(
                                                        [...videos.map((v) => v.basics.surnameAthlete1),
                                                            ...videos.map((v) => v.basics.surnameAthlete2)])
                                                    ],
                                                    getOptionLabel: (athlete) => athlete,
                                                    condition: (object, filter) => filter?.includes(object.basics.surnameAthlete1) || filter?.includes(object.basics.surnameAthlete2) || filter?.length === 0
                                                }
                                            ]}/>,
            progress: deletionInProgress
        })
    }, [deletionInProgress, videos])

    // Show loading indication while loading the list of users
    if (loading) {
        return <LoadingProgressIndication
            text={intl.formatMessage({
                id: 'app.videos.overview.loading',
                defaultMessage: 'Loading videos...'
            })}/>
    }

    // Shows the table containing the videos
    return (
        <Paper className={classes.root}>

            <TableContainer className={classes.container}>
                <Table stickyHeader aria-label="sticky table">

                    {/* Table header */}
                    <TableHead>
                        <TableRow>
                            <StyledTableCell align={"center"} width={"5%"} key='collapsible'></StyledTableCell>
                            <StyledTableCell align={"center"} width={"5%"}
                                             key='numberOfScenes'><VideoLibrary/></StyledTableCell>
                            <StyledTableCell align={"center"} width={"75%"} key='videoCard'>
                                {intl.formatMessage({
                                    id: 'app.videos.overview.table.column.video',
                                    defaultMessage: 'Video'
                                })}
                            </StyledTableCell>
                            <StyledTableCell align={"right"} width={"15%"} key='action'>
                                {intl.formatMessage({
                                    id: 'app.videos.overview.table.column.action',
                                    defaultMessage: 'Actions'
                                })}
                            </StyledTableCell>
                        </TableRow>
                    </TableHead>

                    {/* Table rows */}
                    <TableBody>
                        {filteredVideos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((videoRow, index) => {
                            return (
                                <React.Fragment key={videoRow.key}>
                                    <StyledTableRow hover key={videoRow.key}>
                                        <StyledTableCell key="collapsible">
                                            {openIndex === index
                                                ? <IconButton aria-label="expand row" size="small"
                                                              onClick={() => setOpenIndex(null)}>
                                                    <KeyboardArrowUp/>
                                                </IconButton>
                                                : <IconButton disabled={!videoRow.vimeo.transcoded}
                                                              aria-label="expand row" size="small"
                                                              onClick={() => setOpenIndex(index)}>
                                                    <KeyboardArrowDown/>
                                                </IconButton>}
                                        </StyledTableCell>
                                        <StyledTableCell key="numberOfScenes">
                                            <div className={classes.numberOfScenes}>
                                                {scoredScenesNumbers?.find((v) => v._id === videoRow.key)?.count || '*'}
                                            </div>
                                        </StyledTableCell>
                                        <StyledTableCell key='videoCard'>
                                            <VideoCard video={videoRow} textSize={16} headerSize={18}/>
                                        </StyledTableCell>
                                        <StyledTableCell align={"right"} key='action'>
                                            <IconButton
                                                component={NavLink}
                                                to={'/admin/videos/' + videoRow.key + '/edit'}
                                                size="large">
                                                {isOperationOnObjectAllowed(videoRow)
                                                    ? <Edit/>
                                                    : <Info/>}
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleDeleteVideo(videoRow)}
                                                disabled={!isOperationOnObjectAllowed(videoRow)}
                                                size="large">
                                                {videoRow.key === deletionAttemptedVideo
                                                    ? <CircularProgress style={{height: 20, width: 20}}/>
                                                    : <Delete/>}
                                            </IconButton>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                    
                                    <StyledTableRow>
                                        <StyledTableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={7}>
                                            <Collapse in={openIndex === index} timeout="auto" unmountOnExit>
                                                <Box margin={1}>
                                                    <div className={classes.scenesColumn}>
                                                        <ScoredScenesSection videoKey={videoRow.key}
                                                                             onEdit={(selectedScoredScene) => history.push('/admin/videos/' + videoRow.key + '/scoredScenes/' + selectedScoredScene.key + '/edit')}
                                                                             onScoredScenesUpdated={(scoredScenes) => (updateScoredScenesNumberOfVideos())}
                                                                             editButtonEnabled={true}/>
                                                        <AppButton
                                                            label={intl.formatMessage({
                                                                id: 'app.videos.overview.table.expanded.actions.createScene',
                                                                defaultMessage: 'Create Scene'
                                                            })}
                                                            component={NavLink}
                                                            to={'/admin/videos/' + videoRow.key + '/scoredScenes/new'}
                                                            icon={<AddIcon/>}/>
                                                    </div>
                                                </Box>
                                            </Collapse>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination section */}
            <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={filteredVideos.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
}


export default VideosSection
